import {
  REOPEN_DEFAULT_HOURS,
  REOPEN_DURATION_HOURS,
  reopenActivityLine,
  reopenDayKey,
  reopenFromBounds,
  reopenWindowCoversDay,
  reopenWindowLive,
  runningMonthStart,
  type ProjectActivityItem,
  type ReopenDurationHours,
  type ReopenedPeriodItem,
  type ReopenedWindow,
} from '@hygge/shared';

/**
 * What the screens say about a reopened closed period, kept out of the
 * components so the node-environment tests can reach it. The rules
 * themselves — durations, From bounds, liveness — are `@hygge/shared`'s, the
 * same the server judges with. [HW-963]
 */

const DURATION_LABELS: Record<ReopenDurationHours, string> = {
  1: '1 hour',
  2: '2 hours',
  4: '4 hours',
  8: '8 hours',
  24: '1 day',
};

export const REOPEN_DEFAULT_DURATION = String(REOPEN_DEFAULT_HOURS);

/** The Duration select's options, in the order offered. */
export function reopenDurationOptions(): { value: string; label: string }[] {
  return REOPEN_DURATION_HOURS.map((h) => ({ value: String(h), label: DURATION_LABELS[h] }));
}

/**
 * A `YYYY-MM-DD` day as the DatePicker holds days — local midnight of those
 * digits, so `toDateParam` gives the same key back in any zone.
 */
export function dayKeyToPickerDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** The From picker's min, max and starting day, from the server's UTC-day rule. */
export function reopenPickerBounds(now: Date): { min: Date; max: Date; initial: Date } {
  const b = reopenFromBounds(now);
  return {
    min: dayKeyToPickerDate(b.min),
    max: dayKeyToPickerDate(b.max),
    initial: dayKeyToPickerDate(b.initial),
  };
}

/** The window while it is live, else null — a cached list goes stale by itself. */
export function liveWindow(
  window: ReopenedWindow | null | undefined,
  now: Date
): ReopenedWindow | null {
  return window && reopenWindowLive(window.openUntil, now) ? window : null;
}

function dayOf(instant: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(instant);
}

/**
 * When a window closes, on the viewer's clock: `14:30` on the same day,
 * `11.10 14:30` otherwise. `timeZone` is for the tests; the screens omit it.
 */
export function untilText(openUntil: string, now: Date, timeZone?: string): string {
  const until = new Date(openUntil);
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(until);
  if (dayOf(until, timeZone) === dayOf(now, timeZone)) return time;
  const day = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', timeZone })
    .format(until)
    .replace('/', '.');
  return `${day} ${time}`;
}

/** The Members tab's note beside a member with a live window. */
export function openUntilLabel(openUntil: string, now: Date, timeZone?: string): string {
  return `Open until ${untilText(openUntil, now, timeZone)}`;
}

/** `2026-10-03` → `03.10` — a UTC day key, so no zone is involved. */
export function dayMonth(key: string): string {
  return `${key.slice(8, 10)}.${key.slice(5, 7)}`;
}

/**
 * The reopen dialog's note on a member who already has a live window, or null:
 * a new grant replaces From and the expiry, so a granter widening a window
 * sees what it is before narrowing it by accident.
 */
export function currentWindowLabel(
  window: ReopenedWindow | null | undefined,
  now: Date,
  timeZone?: string
): string | null {
  const live = liveWindow(window, now);
  if (!live) return null;
  return `Currently open from ${dayMonth(live.openFrom)} until ${untilText(live.openUntil, now, timeZone)} — opening again replaces it`;
}

/**
 * The viewer's live windows as one string: project, From and until of each,
 * in a fixed order. My Time's rows carry the server's edit and delete flags
 * from when they were read, so a change here — a window granted, replaced,
 * withdrawn or expired — is what calls for reading them again.
 */
export function liveReopenSignature(periods: ReopenedPeriodItem[], now: Date): string {
  return periods
    .filter((p) => reopenWindowLive(p.openUntil, now))
    .map((p) => `${p.projectId}|${p.openFrom}|${p.openUntil}`)
    .sort()
    .join(',');
}

/** Milliseconds until the soonest live window expires, or null without one. */
export function msUntilNextReopenExpiry(periods: ReopenedPeriodItem[], now: Date): number | null {
  let soonest: number | null = null;
  for (const p of periods) {
    if (!reopenWindowLive(p.openUntil, now)) continue;
    const ms = Date.parse(p.openUntil) - now.getTime();
    if (soonest === null || ms < soonest) soonest = ms;
  }
  return soonest;
}

/** The longest delay a browser timer holds. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * When to re-read the entries after the soonest window expires: a second past
 * it on the viewer's clock. That clock may run ahead of the server's, so the
 * re-read is repeated once after `REOPEN_EXPIRY_RECHECK_MS`. [HW-963]
 */
export function reopenExpiryTimerMs(msUntilExpiry: number): number {
  return Math.min(msUntilExpiry + 1_000, MAX_TIMER_MS);
}

/** The second re-read, wider than a plausible skew between the two clocks. */
export const REOPEN_EXPIRY_RECHECK_MS = 5_000;

/**
 * My Time's notice: one line per live window, soonest to close first, keyed by
 * project — a member has one window per project, while two projects may share
 * a name, From and until. A project that takes no new time (archived, on hold)
 * still lets the member edit and delete what they logged, and the line says
 * only that. Given the shown week's days, a window is left out unless it
 * covers one of them that sits in a closed month — the only days a window
 * changes — so a week with nothing reopened carries no notice.
 */
export function reopenNoticeLines(
  periods: ReopenedPeriodItem[],
  now: Date,
  timeZone?: string,
  weekDays?: readonly string[]
): { key: string; text: string }[] {
  const openMonthStart = reopenDayKey(runningMonthStart(now));
  return periods
    .filter((p) => reopenWindowLive(p.openUntil, now))
    .filter(
      (p) =>
        !weekDays ||
        weekDays.some(
          (day) =>
            day < openMonthStart &&
            reopenWindowCoversDay({ from: p.openFrom, until: p.openUntil }, day, now)
        )
    )
    .sort((a, b) => a.openUntil.localeCompare(b.openUntil))
    .map((p) => ({
      key: p.projectId,
      text: `Closed period open on ${p.projectName} from ${dayMonth(p.openFrom)} until ${untilText(p.openUntil, now, timeZone)} — ${
        p.takesNewTime ? 'add, edit and delete your entries' : 'edit and delete your existing entries'
      }`,
    }));
}

/**
 * The project feed's line for a `time.reopened` row, with the expiry on the
 * viewer's clock — the stored summary writes it in UTC, being one line for
 * every reader. Any other row, or one whose metadata does not read, keeps its
 * stored summary; both are worded by `reopenActivityLine`. "By an admin
 * account" follows `metadata.openedBy`, which the server writes exactly when
 * the granter had no employee profile — a row whose actor was deleted later
 * keeps no actor but was not opened by one. [HW-963]
 */
export function activityLineText(activity: ProjectActivityItem, timeZone?: string): string {
  if (activity.type !== 'time.reopened') return activity.summary;
  const m = activity.metadata ?? {};
  const { employeeName, from, until, hours } = m as Record<string, unknown>;
  if (
    typeof employeeName !== 'string' ||
    typeof from !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(from) ||
    typeof until !== 'string' ||
    !Number.isFinite(Date.parse(until)) ||
    typeof hours !== 'number'
  ) {
    return activity.summary;
  }
  const at = new Date(until);
  const day = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', timeZone })
    .format(at)
    .replace('/', '.');
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(at);
  return reopenActivityLine({
    memberName: employeeName,
    from,
    untilText: `${day} ${time}`,
    hours,
    byAccount: !!m.openedBy,
  });
}
